import React, { FunctionComponent, useEffect, useState } from 'react';
import grey from '@material-ui/core/colors/grey';
import { makeStyles, Button, Box, Grid, Theme, InputLabel, Checkbox, FormHelperText } from '@material-ui/core';
import moment from 'moment';
import jsonpath from 'jsonpath';
import { isArray, forEach } from 'lodash';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DatePicker from '../../../../../components/DatePicker';
import CheckBox from '../../../../../components/Checkbox';
import {
    Address,
    AppointmentType,
    DropDownField,
    InspectionType,
} from '../../../../../store/modules/application/types';
import { Person } from '../../../../../store/modules/person/types';
import { getPersonSimpleName } from '../../../../../utils/utils';
import TimePicker from '../../../../../components/TimePicker';
import { INSPECTION_EVENT_TYPE, PERIODICITY } from '../../../../../store/app.constants';
import InputTextComponent from '../../../../../components/InputTextField';
import AutoCompleteSelect from '../../../../../components/AutoCompleteSelect';
import { pick } from 'lodash';

type AddAppointmentDialogProps = {
    person: Person;
    setIsAddAppointmentModalOpen: Function;
    addAppointment: Function;
    issuedByUsers: any;
    licenseListAppointment: any[];
    appointmentTypes: AppointmentType[];
    inspectionTypes: InspectionType[];
    addBulkAppointments: Function;
    counties?: DropDownField[];
    isCountyFieldMappingPresent: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
    uploadDialog: {
        display: 'flex',
        flexGrow: 1,
    },
    addAppointmentDialogLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,

        textAlign: 'end',
        justifyContent: 'flex-end',
        width: '100%',
        lineHeight: '21px',
        paddingRight: '20px',
        display: 'flex',
    },
    addAppointmentDialogCommentLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,

        textAlign: 'end',
        justifyContent: 'flex-end',
        width: '100%',
        lineHeight: '21px',
        paddingRight: '20px',
        display: 'flex',
        paddingTop: '17px',
    },
    addAppointmentDialogValue: {
        fontSize: 14,
        fontWeight: 450,
        color: grey[600],
        width: '100%',
        lineHeight: '21px',
    },
    allDayCheck: {
        fontSize: 14,
        fontWeight: 450,
        color: grey[600],
        width: '100%',
        lineHeight: '21px',
    },
    addAppointmentDialogMain: {
        width: '100%',
    },
    margin: {
        margin: theme.spacing(1),
    },
    activationModal: {
        padding: 10,
    },
    modalHeader: {
        color: theme.palette.text.secondary,

        fontSize: 20,
        fontWeight: 500,
        marginBottom: 20,
    },
    modalBtn: {
        marginLeft: 20,
    },
    btnContainer: {
        marginTop: 25,
    },
    datePickerContainer: {
        backgroundColor: 'white',
        border: '1px solid #ced4da',
        borderRadius: 5,
        padding: '3px 0px 3px 10px',
        margin: '0px 0px',
    },
    datePicker: {
        margin: 0,
    },
    underline: {
        '&:before': {
            borderBottomColor: 'white',
        },
        '&:after': {
            borderBottomColor: 'white',
        },
        '&:hover:before': {
            borderBottomColor: ['white', '!important'],
        },
    },
    textFields: {
        backgroundColor: 'white',
        border: '1px solid #ced4da',
        width: '100%',
        borderRadius: 5,
        padding: '6px 14px',
        '&:focus': {
            backgroundColor: 'white',
        },
        '&:hover': {
            backgroundColor: ['white', '!important'],
        },
    },
    TimePickerInput: {
        width: '100%',
        borderRadius: '5%',
        '& input': {
            fontSize: '14px',
        },
        '& input::placeholder': {
            fontSize: '14px',
        },
        '&>.MuiOutlinedInput-root': {
            '&>.MuiOutlinedInput-input': {
                padding: 10,
            },
            '&>.MuiInputAdornment-root': {
                '&>.MuiIconButton-root': {
                    padding: 0,
                },
            },
        },
        '&>.MuiOutlinedInput-adornedEnd': {
            paddingRight: '5px',
        },
    },
    TimePickerPadding: {
        paddingLeft: '15px',
        paddingTop: '5px',
    },
    expandIconStyle: {
        fontSize: '16px',
    },
    checkboxLabel: {
        color: '#484848',
        fontSize: 16,
    },
}));
const AddAppointmentDialog: FunctionComponent<AddAppointmentDialogProps> = (props) => {
    const {
        setIsAddAppointmentModalOpen,
        addAppointment,
        issuedByUsers,
        licenseListAppointment,
        person,
        appointmentTypes,
        inspectionTypes,
        addBulkAppointments,
        counties,
        isCountyFieldMappingPresent,
    } = props;

    const [timeDiff, setTimeDiff] = useState<number>(0);
    const [appointmentDate, setAppointmentDate] = useState(moment().format('L'));
    const [appointmentStartTime, setAppointmentStartTime] = useState<string | null>(null);
    const [appointmentEndTime, setAppointmentEndTime] = useState<string | null>(null);
    const [useLicenseeAddress, setUseLicenseeAddress] = useState(false);
    const [stopDate, setStopDate] = useState(moment().format('L'));

    const classes = useStyles();

    useEffect(() => {
        if (useLicenseeAddress) {
            setAddress(person?.address?.line1!);
            setCity(person?.address?.city!);
            setState(person?.address?.state!);
            setZipCode(person?.address?.zipcode!);
            if (isCountyFieldMappingPresent && person?.address?.county) {
                let selectedCounty: DropDownField | null | string | undefined;
                if (counties?.length) {
                    selectedCounty = counties?.find((county: DropDownField | null | string) =>
                        typeof county === 'string'
                            ? person?.address?.county === county
                            : person?.address?.county === county?.value,
                    );
                } else {
                    selectedCounty = person?.address?.county;
                }

                selectedCounty && setCounty(selectedCounty);
            }
        } else {
            setAddress('');
            setCity('');
            setState('');
            setZipCode('');
            //County can be an Object or a string so that is why county is set by null here.
            setCounty(null);
        }
    }, [useLicenseeAddress, person, counties, isCountyFieldMappingPresent]);

    useEffect(() => {
        if (appointmentStartTime && appointmentEndTime) {
            const startTime = moment(appointmentStartTime, 'HH:mm');
            const endTime = moment(appointmentEndTime, 'HH:mm');

            const duration = moment.duration(endTime.diff(startTime));

            setTimeDiff(duration.asMinutes());
        } else {
            setTimeDiff(0);
        }
    }, [appointmentStartTime, appointmentEndTime]);

    const handleChangeAppointmentStartTime = (value: string | null) => {
        setAppointmentStartTime(value);
    };

    const handleChangeAppointmentEndTime = (value: string | null) => {
        setAppointmentEndTime(value);
    };

    const [address, setAddress] = useState<string>('');
    const handleChangeAddress = (address: string) => {
        setAddress(address);
    };

    const [city, setCity] = useState<string>('');
    const handleChangeCity = (city: string) => {
        setCity(city);
    };
    const [state, setState] = useState<string>('');
    const handleChangeState = (state: string) => {
        setState(state);
    };

    const [county, setCounty] = useState<DropDownField | null | string>(null);
    const handleChangeCounty = (data: DropDownField | null | string) => {
        setCounty(data);
    };

    const [zipCode, setZipCode] = useState<string>('');
    const handleChangeZipCode = (zipCode: string) => {
        setZipCode(zipCode);
    };
    const [assignedTo, setAssignedTo] = useState<Array<object>>();

    const [selectedAssignedTo, setSelectedAssignedTo] = useState<DropDownField[]>([]);
    const handleChangeAssignedTo = (data: DropDownField[]) => {
        const assignedValues = data.map((obj: DropDownField) => obj.value);
        const selectedAssignees = issuedByUsers.filter((user: any) =>
            assignedValues.some((value: string) => user.id === value),
        );
        setSelectedAssignedTo(data);
        setAssignedTo(selectedAssignees);
    };

    const [selectedLicenses, setSelectedLicenses] = useState<DropDownField[]>([]);
    const handleSelectedLicenses = (data: DropDownField[]) => {
        setSelectedLicenses(data);
    };

    const [comment, setComment] = useState<string>('');
    const handleChangeComment = (comment: string) => {
        setComment(comment);
    };

    const [appointmentType, setAppointmentType] = useState<DropDownField | null>(null);
    const handleChangeAppointmentType = (data: DropDownField | null) => {
        setAppointmentType(data);
    };

    const [inspectionType, setInspectionType] = useState<DropDownField | null>(null);
    const handleChangeInspectionType = (data: DropDownField | null) => {
        setInspectionType(data);
    };

    const [repeat, setRepeat] = useState<DropDownField | null>();
    const handleChangeRepeats = (data: DropDownField | null) => {
        setRepeat(data);
    };

    const [allDay, setAllDay] = useState<boolean>(false);

    const handleAddAppointment = () => {
        const date = moment(appointmentDate).format('YYYY-MM-DD');

        const startTimeStamp = appointmentStartTime ? moment(date + 'T' + appointmentStartTime).toISOString() : date;
        const endTimeStamp = appointmentEndTime ? moment(date + 'T' + appointmentEndTime).toISOString() : undefined;

        const addressObj: Address = {
            line1: address,
            city: city,
            state: state,
            zipcode: zipCode,
            county: typeof county === 'string' ? county : county?.value,
        };
        const participants = assignedTo?.map((element: any) => {
            return {
                id: element?.id,
            };
        });
        const licensesForPayload: any = [];
        selectedLicenses?.map((element: any) => {
            let licenseId = element?.value;
            if (licenseId) {
                licenseListAppointment.filter((ele) => {
                    if (ele.id === licenseId) {
                        licensesForPayload.push(pick(ele, 'id', 'licenseNumber', 'type'));
                        return ele;
                    }
                    return null;
                });
            }
        });
        const newPayload = {
            startDateTime: allDay ? date : startTimeStamp,
            endDateTime: allDay ? null : endTimeStamp,
            address: addressObj,
            participants: participants,
            comment,
            licenses: licensesForPayload,
            persons: [
                {
                    name: getPersonSimpleName(person?.name),
                    id: person?.id,
                },
            ],
            type: {
                id: appointmentType?.value,
                name: appointmentType?.label,
            },
            inspectionType:
                appointmentType?.value === INSPECTION_EVENT_TYPE
                    ? {
                          id: inspectionType?.value,
                          name: inspectionType?.label,
                      }
                    : undefined,

            repeat: !!repeat?.value && repeat?.value !== 'Does not repeat' ? repeat.value : undefined,
            stopDate:
                !!repeat?.value && repeat?.value !== 'Does not repeat'
                    ? moment(stopDate).format('YYYY-MM-DD')
                    : undefined,
        };

        if (!!repeat?.value && repeat?.value !== 'Does not repeat') {
            addBulkAppointments(newPayload);
        } else {
            addAppointment(newPayload);
        }
        setIsAddAppointmentModalOpen(false);
    };
    const convertTimeToMinAndHours = () => {
        const num = timeDiff;
        const hours = num / 60;
        const rhours = Math.floor(hours);
        const minutes = (hours - rhours) * 60;
        const rminutes = Math.round(minutes);
        if (isNaN(num)) {
            return 0 + ' h ' + 0 + ' m';
        } else {
            return rhours + ' h ' + rminutes + ' m';
        }
    };

    const assignedToMapping = {
        value: '$.id',
        label: '$.name',
    };

    const licenseMapping = {
        value: ['$.id'],
        label: ['$.licenseNumber', '$.rideName'],
    };

    const convertToDropDown = (data: Array<any>, resultMapping: Object): Array<any> => {
        let selectData = data.map((choice: Object) => {
            let choiceMap: any = {};

            forEach(resultMapping, (mapValue: any, mapKey) => {
                if (isArray(mapValue)) {
                    let customValue = mapValue.map((value) => {
                        return jsonpath.value(choice, value);
                    });

                    choiceMap[mapKey] = customValue.join(' - ');
                } else {
                    choiceMap[mapKey] = jsonpath.value(choice, mapValue);
                }
            });

            return choiceMap;
        });
        return selectData;
    };

    const handleChangeAllDay = (isAllDayChecked: boolean) => {
        setAllDay(isAllDayChecked);
    };

    const disableAddButtonHandler = () => {
        const isInvalidAppointmentDate = !moment(appointmentDate).isValid();
        const isInvalidStopDate =
            !!repeat?.value &&
            repeat?.value !== 'Does not repeat' &&
            stopDate &&
            (!moment(stopDate).isValid() || moment(appointmentDate) > moment(stopDate));

        const hasNoAssignee = !assignedTo;
        const hasNoAppointmentType = !appointmentType?.value;

        const isInvalidTime =
            !allDay && appointmentStartTime && appointmentEndTime ? appointmentStartTime > appointmentEndTime : false;

        return isInvalidAppointmentDate || isInvalidStopDate || hasNoAssignee || hasNoAppointmentType || isInvalidTime;
    };

    let users: Array<any> = convertToDropDown(issuedByUsers, assignedToMapping);
    let licenses: Array<any> = convertToDropDown(licenseListAppointment, licenseMapping);

    return (
        <Box className={classes.uploadDialog}>
            <Grid container className={classes.activationModal}>
                <div className={classes.modalHeader}>Add Appointment</div>
                <Box component="div" className={classes.addAppointmentDialogMain}>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addAppointmentDialogLabel}>
                                Appointment Date
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <DatePicker
                                    containerClasses={classes.datePickerContainer}
                                    datePickerClasses={classes.datePicker}
                                    inputProps={{
                                        classes: {
                                            underline: classes.underline,
                                        },
                                    }}
                                    value={appointmentDate}
                                    handleDate={(date: string) => {
                                        setAppointmentDate(date);
                                    }}
                                    fullWidth={true}
                                    label=""
                                    marginNone={true}
                                ></DatePicker>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Box
                                className={classes.allDayCheck}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    marginLeft: '25px',
                                }}
                            >
                                <CheckBox
                                    name={'allDay'}
                                    checked={allDay}
                                    onChange={handleChangeAllDay}
                                    label={'All Day'}
                                    className={classes.checkboxLabel}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addAppointmentDialogLabel}>
                                Time
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Box className={classes.addAppointmentDialogValue} style={{ display: 'flex' }}>
                                <TimePicker
                                    placeholder="00:00 AM"
                                    className={classes.TimePickerInput}
                                    margin="dense"
                                    size="small"
                                    id="start-time"
                                    value={appointmentStartTime}
                                    onChange={handleChangeAppointmentStartTime}
                                    keyboardIcon={<ExpandMoreIcon className={classes.expandIconStyle} />}
                                    disabled={allDay}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={1}>
                            <Box
                                style={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                to
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={2}>
                            <Box className={classes.addAppointmentDialogValue} style={{ justifyContent: 'flex-end' }}>
                                <TimePicker
                                    placeholder="00:00 AM"
                                    className={classes.TimePickerInput}
                                    margin="dense"
                                    size="small"
                                    id="end-time"
                                    value={appointmentEndTime}
                                    onChange={handleChangeAppointmentEndTime}
                                    keyboardIcon={<ExpandMoreIcon className={classes.expandIconStyle} />}
                                    disabled={allDay}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={2}>
                            <Box
                                className={classes.addAppointmentDialogValue}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    marginTop: '7px',
                                    marginLeft: '25px',
                                }}
                            >
                                <Box
                                    component="div"
                                    style={{
                                        width: '75%',
                                        fontSize: '12px',
                                        backgroundColor: '#EEEEEE',
                                        lineHeight: '40px',
                                        textAlign: 'center',
                                    }}
                                >
                                    {convertTimeToMinAndHours()}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}></Grid>
                        <Grid item xs={12} sm={9}>
                            {appointmentStartTime &&
                            appointmentEndTime &&
                            !allDay &&
                            appointmentStartTime > appointmentEndTime ? (
                                <FormHelperText error={true}>Time duration is invalid</FormHelperText>
                            ) : null}
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addAppointmentDialogLabel}>
                                Repeats
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputLabel htmlFor="issued-by-select-native" style={{ display: 'none' }}>
                                    Repeats
                                </InputLabel>
                                <AutoCompleteSelect
                                    id="issued-by-select"
                                    data={PERIODICITY.map((item: any) => ({
                                        label: item.label,
                                        value: item.value,
                                    }))}
                                    onChange={handleChangeRepeats}
                                    value={repeat}
                                    inputSize={'small'}
                                    variant={'outlined'}
                                    ariaLabel="Repeats"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    {!!repeat?.value && repeat?.value !== 'Does not repeat' && (
                        <Grid container style={{ marginTop: '10px' }}>
                            <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                                <Box component="div" className={classes.addAppointmentDialogLabel}>
                                    Repeat ends on
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <Box className={classes.addAppointmentDialogValue}>
                                    <DatePicker
                                        containerClasses={classes.datePickerContainer}
                                        datePickerClasses={classes.datePicker}
                                        inputProps={{
                                            classes: {
                                                underline: classes.underline,
                                            },
                                        }}
                                        value={stopDate}
                                        handleDate={(date: string) => {
                                            setStopDate(date);
                                        }}
                                        fullWidth={true}
                                        label=""
                                        marginNone={true}
                                        minDate={appointmentDate}
                                        minDateMessage="End date cannot be earlier than the appointment date"
                                    ></DatePicker>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addAppointmentDialogLabel}>
                                Assigned To
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputLabel htmlFor="issued-by-select-native" style={{ display: 'none' }}>
                                    Assigned To
                                </InputLabel>
                                <AutoCompleteSelect
                                    data={users}
                                    onChange={handleChangeAssignedTo}
                                    inputSize={'small'}
                                    value={selectedAssignedTo}
                                    variant={'outlined'}
                                    ariaLabel="Assigned To"
                                    isMultipleValue={true}
                                    disableCloseOnSelect={true}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addAppointmentDialogLabel}>
                                Appointment Type
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputLabel htmlFor="issued-by-select-native" style={{ display: 'none' }}>
                                    Appointment Type
                                </InputLabel>
                                <AutoCompleteSelect
                                    id="issued-by-select"
                                    data={appointmentTypes.map((item: any) => ({
                                        label: item.name,
                                        value: item.id,
                                    }))}
                                    onChange={handleChangeAppointmentType}
                                    value={appointmentType}
                                    inputSize={'small'}
                                    variant={'outlined'}
                                    ariaLabel="Appointment Type"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    {appointmentType?.value === INSPECTION_EVENT_TYPE ? (
                        <Grid container style={{ marginTop: '10px' }}>
                            <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                                <Box component="div" className={classes.addAppointmentDialogLabel}>
                                    Inspection Type
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={9}>
                                <Box className={classes.addAppointmentDialogValue}>
                                    <InputLabel htmlFor="inspection-type-select-native" style={{ display: 'none' }}>
                                        Inspection Type
                                    </InputLabel>
                                    <AutoCompleteSelect
                                        id="inspection-type-select"
                                        data={inspectionTypes.map((item: any) => ({
                                            label: item.name,
                                            value: item.id,
                                        }))}
                                        onChange={handleChangeInspectionType}
                                        value={inspectionType}
                                        inputSize={'small'}
                                        variant={'outlined'}
                                        ariaLabel="Inspection Type"
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    ) : null}
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addAppointmentDialogLabel}>
                                Use Licensee Address
                            </Box>
                        </Grid>
                        <Grid item xs={9} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <Checkbox
                                    disabled={!person?.address}
                                    checked={useLicenseeAddress}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setUseLicenseeAddress(e.target.checked)
                                    }
                                    color="primary"
                                    inputProps={{ 'aria-label': 'secondary checkbox' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3}>
                            <Box component="div" className={classes.addAppointmentDialogCommentLabel}>
                                Address
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputTextComponent
                                    id="address"
                                    disabled={false}
                                    size="small"
                                    fontSize="13"
                                    value={address}
                                    className={classes.textFields}
                                    onChange={handleChangeAddress}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3}>
                            <Box component="div" className={classes.addAppointmentDialogCommentLabel}>
                                City
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputTextComponent
                                    id="city"
                                    disabled={false}
                                    size="small"
                                    fontSize="13"
                                    value={city}
                                    className={classes.textFields}
                                    onChange={handleChangeCity}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3}>
                            <Box component="div" className={classes.addAppointmentDialogCommentLabel}>
                                State
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputTextComponent
                                    id="state"
                                    disabled={false}
                                    size="small"
                                    fontSize="13"
                                    value={state}
                                    className={classes.textFields}
                                    onChange={handleChangeState}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addAppointmentDialogLabel}>
                                County
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputLabel htmlFor="county-select-native" style={{ display: 'none' }}>
                                    County
                                </InputLabel>
                                {isCountyFieldMappingPresent && counties?.length ? (
                                    <AutoCompleteSelect
                                        data={counties}
                                        onChange={handleChangeCounty}
                                        inputSize={'small'}
                                        value={county}
                                        variant={'outlined'}
                                        ariaLabel="County"
                                    />
                                ) : (
                                    <InputTextComponent
                                        id="county"
                                        value={typeof county === 'string' ? county : ''}
                                        disabled={false}
                                        size="small"
                                        fontSize="13"
                                        className={classes.textFields}
                                        onChange={handleChangeCounty}
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3}>
                            <Box component="div" className={classes.addAppointmentDialogCommentLabel}>
                                Zip Code
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputTextComponent
                                    id="zipCode"
                                    disabled={false}
                                    size="small"
                                    fontSize="13"
                                    value={zipCode}
                                    className={classes.textFields}
                                    onChange={handleChangeZipCode}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="div" className={classes.addAppointmentDialogLabel}>
                                Select License
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputLabel htmlFor="issued-by-select-native" style={{ display: 'none' }}>
                                    Select License
                                </InputLabel>
                                <AutoCompleteSelect
                                    data={licenses}
                                    isMultipleValue={true}
                                    onChange={handleSelectedLicenses}
                                    value={selectedLicenses}
                                    inputSize={'small'}
                                    variant={'outlined'}
                                    ariaLabel="License"
                                    disableCloseOnSelect={true}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: '10px' }}>
                        <Grid item xs={12} sm={3}>
                            <Box component="div" className={classes.addAppointmentDialogCommentLabel}>
                                Comments
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Box className={classes.addAppointmentDialogValue}>
                                <InputTextComponent
                                    id="comments"
                                    disabled={false}
                                    size="small"
                                    fontSize="13"
                                    value={comment}
                                    multiline={true}
                                    className={classes.textFields}
                                    rows={5}
                                    onChange={handleChangeComment}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <Grid className={classes.btnContainer} sm={12} md={12} lg={12} item>
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            onClick={() => {
                                setIsAddAppointmentModalOpen(false);
                            }}
                            className={classes.modalBtn}
                        >
                            CANCEL
                        </Button>
                        <Button
                            onClick={handleAddAppointment}
                            className={classes.modalBtn}
                            variant="contained"
                            color="primary"
                            disabled={disableAddButtonHandler()}
                        >
                            ADD
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddAppointmentDialog;
